import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/ja";
import "dayjs/locale/zh-cn";
import "dayjs/locale/zh-tw";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { useTranslation } from "react-i18next";
import { useLocalStorage } from "../../context/useLocalStorage";
import localStorageKey from "../../enumerations/localStorageKey";
import { envChangeType, envType, getLang } from "../../plugins/utils";

// Extend dayjs with necessary plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

type InputDate = string | Date | Dayjs | null | undefined;

const useGetTimezone = () => {
  const lang = getLang();
  const { t } = useTranslation();
  const [defaultLanguage, setDefaultLanguage] = useLocalStorage(
    localStorageKey.DEFAULT_LANG,
    "zh-TW"
  );

  const toLocalTimezone = (inputDate: InputDate) => {
    //Z -> 00:00
    if (!inputDate) return null;

    const dateObj = dayjs.isDayjs(inputDate) ? inputDate : dayjs(inputDate);
    return dateObj.utc().tz(process.env.REACT_APP_TIMEZONE);
  };

  const toServerTimezone = (inputDate: InputDate) => {
    //Z+9 ->dayjs().tz(Asia/Tokyo).format() -> +09:00
    if (!inputDate) return null;

    const dateObj = dayjs.isDayjs(inputDate) ? inputDate : dayjs(inputDate);
    return dateObj.utc();
  };

  const formatDate = (date: Dayjs | null) => {
    if (!date) return "";
    return date.format(
      `YYYY${t("format.date.year")}MM${t("format.date.month")}DD${t(
        "format.date.date"
      )}`
    );
  };

  const forceDate = (date: Dayjs | null | any) => {
    if (!date) return "";
    return date.format(
      `YYYY-MM-DDTHH:mm:ss${getTimezoneOffset(process.env.REACT_APP_TIMEZONE)}`
    );
  };

  const getTimezoneOffset = (tzName?: string) => {
    if (!tzName) return;
    const offsetMinutes = dayjs().tz(tzName).utcOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absMinutes = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
    const minutes = String(absMinutes % 60).padStart(2, "0");
    return `${sign}${hours}:${minutes}`;
  };

  const formatSmartDate = (inputDate: InputDate) => {
    // Map locale input to dayjs-supported locale codes

    const dayjsLocale = defaultLanguage.toLowerCase();
    dayjs.locale(dayjsLocale);

    const now = dayjs();
    const date = dayjs(inputDate);
    const diffHours = now.diff(date, "hour");

    if (diffHours < 24) {
      return date.fromNow();
    } else {
      return date.format("YYYY/MM/DD");
    }
  };

  const isAlreadyPassTimeLimit = (): boolean => {
    const limit = envChangeType(
      "REACT_APP_LIMIT_NEXT_DAY_ORDER",
      envType.NUMBER
    );
    const now = toLocalTimezone(dayjs());
    const sevenPM = now?.startOf("day").add(limit, "hour");
    return now?.isAfter(sevenPM) || false;
  };

  return {
    toLocalTimezone,
    toServerTimezone,
    formatDate,
    getTimezoneOffset,
    forceDate,
    formatSmartDate,
    isAlreadyPassTimeLimit,
  };
};

export default useGetTimezone;
